import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAccount, useNetwork, useWaitForTransaction, useContractRead, useContractWrite } from 'wagmi';
import { Web3Button } from "@web3modal/react";
import BigNumber from 'bignumber.js';
import { useCookies } from 'react-cookie';

import { NETWORK, ADDRESSES, ABIS, APIURL } from "../config";

import Footer from '../components/Footer';
import { SwitchNetwork } from '../components/SwitchNetwork';

import biofiLogo from '../images/WebLogoWht.svg';

import ethosDownload from '../images/EthosDL.png';
import iosDownload from '../images/iOS_Download_Btn.svg';
import androidAPKDownload from '../images/Android_APK_Download_Btn.svg';
import androidDownload from '../images/Android_Download_Btn.svg';
import refreshIcon from '../images/refresh-symbol.svg';
import SessionHandler from '../components/SessionHandler';


function addLeadingZero(num) {
  num = num.toString();
  if (num.length < 2) {
    num = "0" + num;
  }
  return num;
}

function convertDate(timestamp) {
  let myDate = new Date(timestamp);
  let dateString = myDate.getFullYear() + "-" + addLeadingZero(myDate.getMonth() + 1) + "-" + addLeadingZero(myDate.getDate());

  return dateString;
}

function convertDecimals(input) {
  return input / 10 ** 18;
}

function convertToDecimals(input) {
  return input * 10 ** 18;
}


function convertBioFiAmount(amount) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
  let convertedString = formatter.format(amount);

  return convertedString.slice(1);
}

export default function DashboardOld({ validChain }) {

  const [userAccountType, setUserAccountType] = useState('-');

  const [userBiofiBalance, setUserBiofiBalance] = useState('0.00000000');
  const [cookies] = useCookies();

  const [perksModalActive, setPerksModalActive] = useState(false);
  const [addTokensModalActive, setAddTokensModalActive] = useState(false);
  const [upgradeModalActive, setUpgradeModalActive] = useState(false);

  const userStakeData = useRef(null);
  const tierData = useRef(null);
  const [userStakeTier, setUserStakeTier] = useState(null);
  const [displayStakeData, setDisplayStakeData] = useState(null);
  const [userActivityCount, setUserActivityCount] = useState(null);
  const [userAccessCount, setUserAccessCount] = useState(null);
  const [addTokensAmount, setAddTokensAmount] = useState(0);
  const [userTaskList, setUserTaskList] = useState(null);
  const [selectedTier, setSelectedTier] = useState(0);
  const [userStakeExists, setUserStakeExists] = useState(null);


  //input fields for tasks
  const [taskDiscordAMA, setTaskDiscordAMA] = useState("");
  const [taskTwitterAMA, setTaskTwitterAMA] = useState("");
  const [taskTelegramAMA, setTaskTelegramAMA] = useState("");
  const [taskRetweet, setTaskRetweet] = useState("");
  const [taskNewsletter, setTaskNewsletter] = useState("");
  const [taskReferTwitter, setTaskReferTwitter] = useState("");
  const [taskReferNewsletter, setTaskReferNewsletter] = useState("");
  const [taskReferAMA, setTaskReferAMA] = useState("");


  const { address } = useAccount();
  let isWalletConnected = (address !== null && typeof (address) !== 'undefined');

  const { chain } = useNetwork();
  let isNetworkCorrect = chain?.id === validChain?.id;

  useEffect(() => {
    let userType = cookies.sessionUserType;
    if (userType === undefined) {
      userType = "user";
    }
    setUserAccountType(userType);
  }, []);


  const useReadUserBalance = useContractRead({
    address: ADDRESSES[NETWORK.id].biofi,
    abi: ABIS.biofi,
    functionName: "balanceOf",
    enabled: address !== null && typeof (address) !== 'undefined',
    args: [address],
    watch: false,
  });

  const useReadUserStake = useContractRead({
    address: ADDRESSES[NETWORK.id].stakingOld,
    abi: ABIS.stakingOld,
    functionName: "readStake",
    enabled: address !== null && typeof (address) !== 'undefined',
    args: [address],
    watch: false,
  });

  const useReadStakeTier = useContractRead({
    address: ADDRESSES[NETWORK.id].stakingOld,
    abi: ABIS.stakingOld,
    functionName: "tiers",
    enabled: userStakeTier !== null && typeof (userStakeTier) !== 'undefined',
    args: [userStakeTier],
    watch: false,
  });

  const useReadPrincipalInterest = useContractRead({
    address: ADDRESSES[NETWORK.id].stakingOld,
    abi: ABIS.stakingOld,
    functionName: "readPrincipalInterest",
    enabled: address !== null && typeof (address) !== 'undefined' && userActivityCount !== null && typeof (userActivityCount) !== 'undefined',
    args: [address, userActivityCount],
    watch: false,
  });

  const useWriteApprove = useContractWrite({
    address: ADDRESSES[NETWORK.id].biofi,
    abi: ABIS.biofi,
    functionName: "approve",
    onError(error) {
      console.log('Error', error)
      toast.error("Adding tokens failed - try again");
    },
    onSuccess(data) {
      console.log('topUpStake tx submitted', data);
      toast("Transaction pending...");
      const txResult = waitForApproval;
      console.log(txResult);
    },
  });

  const useWriteAddTokens = useContractWrite({
    address: ADDRESSES[NETWORK.id].stakingOld,
    abi: ABIS.stakingOld,
    functionName: "topUpStake",
    onError(error) {
      console.log('Error', error)
      toast.error("Adding tokens failed - try again");
    },
    onSuccess(data) {
      console.log('topUpStake tx submitted', data);
      toast("Transaction pending...");
      const txResult = waitForAddTokens;
      console.log(txResult);
    },
  });

  const useWriteUpgradeStake = useContractWrite({
    address: ADDRESSES[NETWORK.id].stakingOld,
    abi: ABIS.stakingOld,
    functionName: "upgradeStake",
    onError(error) {
      console.log('Error', error);
      toast.error("Upgrade failed - try again");
    },
    onSuccess(data) {
      console.log('upgrade tx submitted', data);
      toast("Transaction pending...");
      const txResult = waitForUpgradeStake;
      console.log(txResult);
    },
  });

  const useWriteCloseStake = useContractWrite({
    address: ADDRESSES[NETWORK.id].stakingOld,
    abi: ABIS.stakingOld,
    functionName: "closeStake",
    onError(error) {
      console.log('Error', error);
      toast.error("Closing rewards contract failed - try again");
    },
    onSuccess(data) {
      console.log('close tx submitted', data);
      toast("Transaction pending...");
      const txResult = waitForCloseStake;
      console.log(txResult);
    },
  });

  const waitForApproval = useWaitForTransaction({
    hash: useWriteApprove.data?.hash,
    onSuccess(data) {
      toast.success("Approved successfully");
      console.log('Approved successfully', data);
    },
  });

  const waitForAddTokens = useWaitForTransaction({
    hash: useWriteAddTokens.data?.hash,
    onSuccess(data) {
      toast.success("Added tokens successfully");
      console.log('Added tokens successfully', data);
      setAddTokensModalActive(false);
    },
  });

  const waitForUpgradeStake = useWaitForTransaction({
    hash: useWriteUpgradeStake.data?.hash,
    onSuccess(data) {
      toast.success("Added tokens successfully");
      console.log('Added tokens successfully', data);
      setUpgradeModalActive(false);
    },
  });

  const waitForCloseStake = useWaitForTransaction({
    hash: useWriteCloseStake.data?.hash,
    onSuccess(data) {
      toast.success("Rewards contract closed successfully");
      console.log('Rewards contract closed successfully', data);
      fetchStakeData();
    },
  });

  function formatInteger(n) {
    let string = String(n);
    if (string.includes('.')) {
      let parts = string.split('.');
      let first = parts[0].replace(/(.)(?=(\d{3})+$)/g, '$1,');
      let result = first + '.' + parts[1];
      return result;
    }
    return String(n).replace(/(.)(?=(\d{3})+$)/g, '$1,');
  }

  async function fetchUserBalance() {
    console.log('fetchUserBalance');
    if (useReadUserBalance.data === undefined) return;

    let userBalance = BigNumber(useReadUserBalance.data.toString()).dividedBy(10 ** 18);
    let convertedUserBalance = formatInteger(userBalance);

    setUserBiofiBalance(convertedUserBalance.toString());

  }

  async function fetchUserStake() {
    console.log('fetchUserStake');
    if (useReadUserStake.data === undefined) return;

    let stakeInfo = useReadUserStake.data;
    console.log(stakeInfo);


    setUserStakeTier(stakeInfo.tierId);

    let convertedStakeInfo = {};
    convertedStakeInfo.exists = stakeInfo.exists;

    userStakeData.current = stakeInfo;

  }


  async function fetchStakeTier() {
    console.log('fetchStakeTier');
    if (useReadStakeTier.data === undefined) return;
    console.log('fetchStakeTier ok');

    let stakeTierInfo = useReadStakeTier.data;
    console.log(stakeTierInfo);

    let convertedTierInfo = {};

    convertedTierInfo.isActive = stakeTierInfo[0];
    convertedTierInfo.name = stakeTierInfo[1];
    convertedTierInfo.requiredActivities = stakeTierInfo[2].toString();
    convertedTierInfo.aprNumerator = stakeTierInfo[3].toString();
    convertedTierInfo.aprBonusNumerator = stakeTierInfo[4].toString();
    convertedTierInfo.aprDenominator = stakeTierInfo[5].toString();
    convertedTierInfo.minStake = stakeTierInfo[6].toString();
    convertedTierInfo.maxStake = stakeTierInfo[7].toString();
    convertedTierInfo.stakeDuration = stakeTierInfo[8].toString();

    console.log("convertedTierInfo", convertedTierInfo);
    tierData.current = convertedTierInfo;
  }

  function updateDisplayStakeData() {
    console.log("updateDisplayStakeData");
    if (tierData.current === null) return;
    if (userStakeData.current === null) return;

    if (!userStakeData.current.exists) {
      setUserStakeExists(false);
    } else {
      setUserStakeExists(true);
    }

    if (useReadPrincipalInterest.data === undefined) return;

    let newDisplayStakeData = {};

    let durationNum = parseInt(tierData.current.stakeDuration);

    if (userStakeData.current.exists) {

      let apr = parseInt(tierData.current.aprNumerator) - parseInt(tierData.current.aprDenominator);
      let aprBonus = parseInt(tierData.current.aprBonusNumerator) - parseInt(tierData.current.aprDenominator);
      let now = new Date().getTime() / 1000;
      let stakeReadyToClose = userStakeData.current.completionTimestamp <= now;
      let convertedStakeAmount = convertDecimals(parseInt(userStakeData.current.stakedAmount));
      let projectedRewards = (convertedStakeAmount * apr / 100) * (tierData.current.stakeDuration / (24 * 60 * 60)) / 365;
      let projectedBonusRewards = (convertedStakeAmount * aprBonus / 100) * (tierData.current.stakeDuration / (24 * 60 * 60)) / 365;


      newDisplayStakeData.exists = true;
      newDisplayStakeData.stakeReadyToClose = stakeReadyToClose;
      newDisplayStakeData.name = tierData.current.name;
      newDisplayStakeData.startDate = convertDate(parseInt(userStakeData.current.startTimestamp) * 1000);
      newDisplayStakeData.duration = tierData.current.stakeDuration;
      newDisplayStakeData.durationConverted = (durationNum % (24 * 60 * 60) === 0) ? (durationNum / (24 * 60 * 60)) + ' days' : durationNum + 's';
      newDisplayStakeData.usageCompleted = 0;
      newDisplayStakeData.endDate = convertDate(parseInt(userStakeData.current.completionTimestamp) * 1000);
      newDisplayStakeData.baseRewards = convertBioFiAmount(projectedRewards);
      newDisplayStakeData.maxRewards = convertBioFiAmount(projectedBonusRewards);
      newDisplayStakeData.stakedAmount = convertBioFiAmount(convertedStakeAmount);
      newDisplayStakeData.requiredActivities = tierData.current.requiredActivities;
      newDisplayStakeData.interestEarned = convertBioFiAmount(convertDecimals(parseInt(useReadPrincipalInterest.data[1]) + parseInt(useReadPrincipalInterest.data[2])));


    } else {
      newDisplayStakeData.exists = false;
    }

    setDisplayStakeData(newDisplayStakeData);
  }



  function fetchStakeData() {
    console.log('refetching all onchain data...');

    useReadUserBalance.refetch();
    useReadUserStake.refetch();
    useReadStakeTier.refetch();
    useReadPrincipalInterest.refetch();

  }

  function togglePerksModal() {
    setPerksModalActive(!perksModalActive);
  }

  function toggleAddTokensModal() {
    setAddTokensModalActive(!addTokensModalActive);
  }

  function toggleUpgradeModal() {
    setUpgradeModalActive(!upgradeModalActive);
  }

  async function fetchTaskProofs() {
    let taskProofs = [];

    let url = "https://api.biofitoken.io/biofiportal/biofi/staking/getUserTaskProofs?userAddress=" + address;

    let response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
    });

    if (response.ok) {
      let jsonRes = await response.json();
      console.log('Request succeeded with JSON response', jsonRes);
      if (jsonRes.success) {
        taskProofs = jsonRes.data;
      } else {
        taskProofs = [];
      }
      toast.info("Please confirm the transaction in your wallet");
      return taskProofs;
    } else {
      toast.error("Error retrieving task data. Try again later");
      return null;
    }

  }

  async function fetchTaskProof() {
    let taskProof = [];

    let url = APIURL + "/biofiportal/biofi/staking/getUserTaskProof?userAddress=" + address;

    let response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
    });

    if (response.ok) {
      let jsonRes = await response.json();
      console.log('Request succeeded with JSON response', jsonRes);
      if (jsonRes.success) {
        taskProof = jsonRes.data;
      } else {
        taskProof = {};
      }
      toast.info("Please confirm the transaction in your wallet");
      return taskProof;
    } else {
      toast.error("Error retrieving task data. Try again later");
      return null;
    }

  }

  //send tx functions
  async function upgradeStake() {
    console.log("upgradeStake");

    if (selectedTier === undefined) return;

    let taskProof = await fetchTaskProof();

    try {
      useWriteUpgradeStake.write({
        args: [selectedTier, taskProof.taskCount, taskProof.expiresAt, taskProof.signature]
      });
    } catch (e) {
      console.error(e);
    }

  }

  async function closeStake() {
    console.log("closeStake");

    let taskProof = await fetchTaskProof();

    try {
      useWriteCloseStake.write({
        args: [taskProof.taskCount, taskProof.expiresAt, taskProof.signature]
      });
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchActivityCount() {
    console.log("fetchActivityCount");
    if (address === undefined) return;

    let activityCount = 0;
    let accessCount = 0;

    try {

      let url = "https://api.biofitoken.io/biofiportal/biofi/staking/completedUsage?userAddress=" + address;
      /*
      let authHeader = 'bearer ' + biofiBearerCookie;
      let authHeader = 'bearer ';

      let response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'same-origin',
        headers: {
          'Authorization': authHeader
        }
      });
      */

      let response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
      });

      if (response.ok) {
        let jsonRes = await response.json();
        console.log('Request succeeded with JSON response', jsonRes);
        //return { usage: jsonRes.data, access: jsonRes.accessCount };
        activityCount = jsonRes.data;
        accessCount = jsonRes.accessCount;
      }

    } catch (e) {
      console.log(e);
    }
    setUserActivityCount(activityCount);
    setUserAccessCount(accessCount);
  }

  async function fetchUserTasks() {
    console.log("fetchUserTasks");
    if (address === undefined) return;

    let taskList = [];

    try {

      let url = "https://api.biofitoken.io/biofiportal/biofi/staking/getUserTasks?userAddress=" + address;

      let response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
      });

      if (response.ok) {
        let jsonRes = await response.json();
        console.log('Request succeeded with JSON response', jsonRes);
        taskList = jsonRes.data;
      }


    } catch (e) {
      console.log(e);
    }
    setUserTaskList(taskList);
  }

  async function addTokensSubmit(event) {
    if (addTokensAmount === undefined || addTokensAmount <= 0) return;
    console.log('Adding tokens: ' + addTokensAmount);

    toast("Approve BioFi spending...");

    event.preventDefault();
    let amount = convertToDecimals(addTokensAmount);

    try {
      useWriteApprove.write({
        args: [ADDRESSES[NETWORK.id].staking, amount]
      });
    } catch (e) {
      console.error(e);
    }

  }

  async function addTokens() {
    console.log("addTokens");
    if (addTokensAmount === undefined || addTokensAmount <= 0) return;
    let taskProof = await fetchTaskProof();

    let amount = convertToDecimals(addTokensAmount);

    try {
      useWriteAddTokens.write({
        args: [amount, taskProof.taskCount, taskProof.expiresAt, taskProof.signature]
      });
    } catch (e) {
      console.error(e);
    }

  }

  function addTokensChange(newTokenAmount) {
    //todo: validate?
    setAddTokensAmount(newTokenAmount);
  }


  useEffect(() => {
    fetchUserBalance();
  }, [useReadUserBalance.data]);

  useEffect(() => {
    fetchUserStake();
  }, [useReadUserStake.data]);

  useEffect(() => {
    fetchStakeTier();
  }, [useReadStakeTier.data]);

  useEffect(() => {
    fetchActivityCount();
    fetchUserTasks();
  }, [address]);

  useEffect(() => {
    updateDisplayStakeData();
  }, [userStakeData.current, tierData.current, useReadPrincipalInterest.data]);


  useEffect(() => {
    addTokens();
  }, [
    waitForApproval.isSuccess
  ]);

  function selectedTierChange(newValue) {
    setSelectedTier(newValue);
  }

  async function confirmTask(taskId, proof) {
    console.log("confirmTask", taskId, proof);

    if (address === undefined) return;

    let userIdString = '';
    if (typeof (proof) !== 'undefined' && proof !== null && proof !== '') {
      userIdString = '&userId=' + proof;
    }

    let url = "https://api.biofitoken.io/biofiportal/biofi/staking/completeTask?userAddress=" + address + "&userTask=" + taskId + userIdString;

    let response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
    });

    if (response.ok) {
      let jsonRes = await response.json();
      console.log('Request succeeded with JSON response', jsonRes);
      toast.success("Task completed successfully");
    } else {
      toast.error("Error completing task. Try again later");
    }

  }

  function sessionValid() {
    return (cookies.sessionUserType !== undefined && cookies.sessionUser !== undefined && cookies.sessionSignature !== undefined && cookies.sessionExpiringAt !== undefined && cookies.sessionExpiringAt > Date.now());
  }

  return (
    <>
      <nav>
        <div className="bf-nav1">
          <div className="bf-navlogo">
            <Link to="/" className="bf-nav-logo bf-nav-brand">
              <img className="bf-navlogo" src={biofiLogo} alt="BioFi" />
            </Link>
          </div>

          <Web3Button icon="show" label="Connect Wallet" balance="hide" />
        </div>
      </nav>
      {
        isNetworkCorrect ? (
          <>
            <div className="bf-dash">
              <div className="bf-dashcont">
                <div className="bf-dash-lft bf-dashcont-el">
                  <h1> <span id="userType"> {userAccountType} </span> Dashboard v2:</h1>
                  <div className="bf-dsh-bal"> $BioFi: <span className="bf-dshinput" > {userBiofiBalance}</span></div>
                  <div className="bf-info">
                    <span className="bf-infotxt">INFORMATION:</span>
                    <div className="bf-infolist"><Link to="/biofiRewards" className="bf-popbtn"> Rewards </Link> | <a className="bf-popbtn" href="#findBiofi">Find $BioFi</a>  | <a href="https://info.biometricfinancial.org/faq/" target="_blank" rel="noreferrer">FAQ</a> </div>
                  </div>
                  <div id="userMenu" className="bf-usrlvlAccess" style={cookies.sessionUserType === 'user' ? { display: 'block' } : { display: 'none' }}>
                    <div className="bf-utilBox2">
                      <div className="bf-info">
                        <span className="bf-infotxt"> User Menu:</span>
                        <div className="bf-infolist"> <button className="button-link" onClick={togglePerksModal}> Perks </button> | <a href="https://ethosmetaverse.net/mydoges.html">View NFTs</a> </div>
                      </div>
                    </div>
                  </div>
                  <div id="prvdrMenu" className="bf-usrlvlAccess" style={cookies.sessionUserType === 'provider' ? { display: 'block' } : { display: 'none' }}>
                    <div className="bf-utilBox2">
                      <div className="bf-info">
                        <span className="bf-infotxt"> Provider Menu:</span>
                        <div className="bf-infolist">
                          <a className="bf-popbtn" target="_blank" rel="noreferrer" href="https://biometricfinancial.org/provider-form/"> Register </a> |
                          { /*<a className="bf-popbtn" href="BiofiCustomerRegistration.html"> Access API </a> |*/}
                          <button className="button-link" onClick={togglePerksModal}> Perks </button> |&nbsp;
                          <a href="https://ethosmetaverse.net/mydoges.html" target="_blank" rel="noreferrer">View NFTs</a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div id="eventMgrMenu" className="bf-usrlvlAccess" style={cookies.sessionUserType === 'eventowner' ? { display: 'block' } : { display: 'none' }}>
                    <div className="bf-utilBox2">
                      <div className="bf-info">
                        <span className="bf-infotxt"> Event Manager Menu:</span>
                        <div className="bf-infolist">
                          {/*<a className="bf-popbtn" href="BiofiCustomerRegistration.html"> Integrate</a> */} |
                          <button className="button-link" onClick={togglePerksModal}> Perks </button> |
                          <a href="https://ethosmetaverse.net/mydoges.html" target="_blank" rel="noreferrer">View NFTs</a> </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'block' }}>
                    <div className="bf-utilBox2">
                      <div className="bf-info">
                        <span className="bf-infotxt">Utility Usage Information</span>
                        <div className="info-el"> Usage Completed: <span className="color-teal">{userActivityCount}</span></div>
                        <div className="info-el"> Access Counter: <span className="color-teal">{userAccessCount}</span> </div>
                        <div className="bf-infolist"> <a className="bf-popbtn" href="https://info.biometricfinancial.org/faq/" target="_blank" rel="noreferrer"> Utility Usage FAQ</a> </div>
                      </div>
                    </div>
                  </div>
                </div>
                {
                  displayStakeData === null && userStakeExists === null ? (

                    <div id="loadingContracts" className="bf-dashcont-el">
                      <div className="bf-flex-rt">
                        <div className="bf-dash-rewards">
                          <div className="bf-nctxt">
                            LOADING...
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    userStakeExists && displayStakeData?.exists ? (

                      <div id="activeContract" className="bf-dashcont-el">
                        <div className="bf-flex-rt">
                          <div className="bf-dash-rewards">
                            <div className="bf-rwtop"><span className="bf-rwhdr"> $BioFi Rewards: </span></div>
                            <div className="bf-rw-mid1">
                              <div className="bf-rw-mdlft">
                                <div className="bf-dash-rwinfo"><span className="bf-rwtxt">Offer:</span><span className="stake-details">{displayStakeData?.name}</span></div>
                                <div className="bf-dash-rwinfo"><span className="bf-rwtxt">Start Date:</span><span className="stake-details">{displayStakeData?.startDate}</span></div>
                                <div className="bf-dash-rwinfo"><span className="bf-rwtxt">Duration:</span><span className="stake-details">{displayStakeData?.durationConverted}</span></div>
                                <div className="bf-dash-rwinfo"><span className="bf-rwtxt">Usage Completed:</span><span className="stake-details">{userActivityCount ? userActivityCount : 0}</span></div>
                                <div className="bf-dash-rwinfo"><span className="bf-rwtxt">End Date:</span><span className="stake-details">{displayStakeData?.endDate}</span></div>
                              </div>
                              <div className="bf-rw-mdrt">
                                <div className="bf-dash-rwinfo"><span className="bf-rwtxt">Base Rewards:</span><span className="stake-details">{displayStakeData?.baseRewards} $BioFi</span></div>
                                <div className="bf-dash-rwinfo"><span className="bf-rwtxt">Max Rewards:</span><span className="stake-details">{displayStakeData?.maxRewards} $BioFi</span></div>
                                <div className="bf-dash-rwinfo"><span className="bf-rwtxt">Locked $BioFi:</span><span className="stake-details">{displayStakeData?.stakedAmount}</span> </div>
                                <div className="bf-dash-rwinfo"><span className="bf-rwtxt">Utility Usage Requirement:</span> <span className="stake-details">{displayStakeData?.requiredActivities}</span></div>
                                <div className="bf-dash-rwinfo"><span className="bf-rwtxt">$BioFi Rewards To Date:</span><span className="stake-details">{displayStakeData?.interestEarned}</span></div>
                              </div>
                              <div className="bf-dash-contfunct">
                                <span>Contract Functions:</span>
                                <div className="bf-functions-wrapper">
                                  <button className="bf-clAdd" id="addTokenButton" onClick={toggleAddTokensModal} disabled={displayStakeData?.stakeReadyToClose}>Add Tokens</button>
                                  <button className="bf-clUpgrade" id="upContractBtn" onClick={toggleUpgradeModal}>Upgrade</button>
                                </div>
                                <div className="bf-infolist"> <a className="bf-popbtn" href="https://info.biometricfinancial.org/faq/" target="_blank" rel="noreferrer"> Add/Upgrade FAQ</a> </div>
                              </div>
                              <div className="bf-dash-contfunct-row">
                                <div className="child-left">
                                  <div className="bf-dash-refresh"> <button className="bf-refreshbtn" id="refreshStakeBtn" onClick={fetchStakeData}><img src={refreshIcon} alt="" />Refresh Rewards Data</button></div>
                                </div>
                                <div className="child-right">
                                  <div>
                                    <span className="bf-rtctxt"> Ready to close?</span> <span>{displayStakeData?.stakeReadyToClose ? "Yes" : "No"}</span>
                                  </div>
                                  <div className="bf-rwbtm">
                                    <button className={displayStakeData?.stakeReadyToClose ? "bf-clContract active" : "bf-clContract"} id="closeContractButton" onClick={closeStake} disabled={!displayStakeData?.stakeReadyToClose}>Close Contract</button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (

                      <div id="noContractActive" className="bf-dashcont-el">
                        <div className="bf-flex-rt">
                          <div className="bf-dash-rewards">
                            <div className="bf-rwtop"><span className="bf-rwhdr"> $BioFi Rewards: </span></div>
                            <div className="bf-nctxt">
                              NO CONTRACTS OPEN!
                              <span> <Link to="/biofiRewards" id="stakingButton" className="bf-stk-btn1"> Click here to open contract!</Link></span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  )
                }
              </div>
            </div>

            <main className="bf-dashmain">
              <div id="manualTaskList" className="bf-taskMenu">
                <h2>Utility Usage Task Checklist:</h2>
                <p>
                  After you've completed any of the following tasks, click the "Confirm" button to receive a point.
                </p>
                <hr />
                <br /><br />
                <div className="task-wrapper">
                  <h3>Download Ethos Metaverse</h3>
                  <p>If you've installed Ethos Metaverse, click the confirm button. You can get it <a href="https://ethosmetaverse.net/" target="_blank" rel="noreferrer">here</a></p>
                  <button id="btn-task-metaverse" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(8)} onClick={() => confirmTask(8)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Download PhenixLoc</h3>
                  <p>If you've installed PhenixLoc, click the confirm button. You can find the download links in your perks on the dashboard</p>
                  <button id="btn-task-usb" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(1)} onClick={() => confirmTask(1)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Attend a Discord AMA</h3>
                  <p>If you've attended at least one Discord AMA, enter your Discord ID in the field below and click the confirm button.</p>
                  <label htmlFor="discordId">Enter your Discord ID</label>
                  <input type="text" className="input-dashboard-standard" id="discordId" placeholder="username#1234" value={taskDiscordAMA} onChange={(e) => setTaskDiscordAMA(e.target.value)} />
                  <button id="btn-task-discord" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(13)} onClick={() => confirmTask(13, taskDiscordAMA)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Attend a Twitter AMA</h3>
                  <p>If you've attended at least one Twitter AMA, enter your Twitter handle in the field below and click the confirm button.</p>
                  <label htmlFor="twitterHandle">Enter your Twitter handle</label>
                  <input type="text" className="input-dashboard-standard" id="twitterHandle" placeholder="@username" value={taskTwitterAMA} onChange={(e) => setTaskTwitterAMA(e.target.value)} />
                  <button id="btn-task-twitter" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(14)} onClick={() => confirmTask(14, taskTwitterAMA)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Attend a Telegram AMA</h3>
                  <p>If you've attended at least one Telegram AMA, enter your Telegram ID in the field below and click the confirm button.</p>
                  <label htmlFor="telegramId">Enter your Telegram ID</label>
                  <input type="text" className="input-dashboard-standard" id="telegramId" placeholder="@username" value={taskTelegramAMA} onChange={(e) => setTaskTelegramAMA(e.target.value)} />
                  <button id="btn-task-telegram" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(15)} onClick={() => confirmTask(15, taskTelegramAMA)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Participate in a race on OpenDao's racing page</h3>
                  <p>If you've participated in at least one race on <a href="https://race.opendao.cloud" target="_blank" rel="noreferrer">https://race.opendao.cloud</a>, using your current wallet address, click the button below.</p>
                  <button id="btn-task-racing" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(16)} onClick={() => confirmTask(16)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Buy a doge</h3>
                  <p>If you hold at least one doge in your current wallet, click the button below. You can purchase a Doge <a href="https://ethosmetaverse.net/dogemint.html" target="_blank" rel="noreferrer">here</a></p>
                  <button id="btn-task-doge" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(17)} onClick={() => confirmTask(17)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Visit the arcade in the Metaverse</h3>
                  <p>If you've been to the arcade in the Metaverse, click the button below. You can download Ethos Metaverse <a href="https://ethosmetaverse.net/" target="_blank" rel="noreferrer">here</a></p>
                  <button id="btn-task-arcade" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(18)} onClick={() => confirmTask(18)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Play the zombie game in the Metaverse</h3>
                  <p>If you've played the zombie game in the Metaverse, click the button below. You can download Ethos Metaverse <a href="https://ethosmetaverse.net/" target="_blank">here</a></p>
                  <button id="btn-task-zombies" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(19)} onClick={() => confirmTask(19)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Retweet a tweet</h3>
                  <p>Retweet any post from the Finnovant Twitter page: <a href="https://twitter.com/Finnovant" target="_blank" rel="noreferrer">Finnovant on Twitter</a></p>
                  <label htmlFor="twitterHandle2">Enter your Twitter handle</label>
                  <input type="text" className="input-dashboard-standard" id="twitterHandle2" placeholder="@username" value={taskRetweet} onChange={(e) => setTaskRetweet(e.target.value)} />
                  <button id="btn-task-retweet" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(20)} onClick={() => confirmTask(20, taskRetweet)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Sign up for our newsletter</h3>
                  <p>Sign up for Finnovant's newsletter: <a href="https://www.finnovant.com/newsletters/" target="_blank" rel="noreferrer">Newsletter</a></p>
                  <label htmlFor="email">Enter your e-mail address</label>
                  <input type="text" className="input-dashboard-standard" id="email" placeholder="user@domain.com" value={taskNewsletter} onChange={(e) => setTaskNewsletter(e.target.value)} />
                  <button id="btn-task-newsletter" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(21)} onClick={() => confirmTask(21, taskNewsletter)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Refer a friend to follow Finnovant on Twitter</h3>
                  <p>Refer a friend to follow the Finnovant profile: <a href="https://twitter.com/Finnovant" target="_blank" rel="noreferrer">Finnovant on Twitter</a></p>
                  <label htmlFor="friendTwitter">Enter your friend's Twitter handle</label>
                  <input type="text" className="input-dashboard-standard" id="friendTwitter" placeholder="@username" value={taskReferTwitter} onChange={(e) => setTaskReferTwitter(e.target.value)} />
                  <button id="btn-task-refer-twitter" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(22)} onClick={() => confirmTask(22, taskReferTwitter)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Refer a friend to sign up for our newsletter</h3>
                  <p>Refer a friend to sign up for Finnovant's newsletter: <a href="https://www.finnovant.com/newsletters/" target="_blank" rel="noreferrer">Newsletter</a></p>
                  <label htmlFor="friendEmail">Enter your friend's e-mail address</label>
                  <input type="text" className="input-dashboard-standard" id="friendEmail" placeholder="user@domain.com" value={taskReferNewsletter} onChange={(e) => setTaskReferNewsletter(e.target.value)} />
                  <button id="btn-task-refer-newsletter" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(23)} onClick={() => confirmTask(23, taskReferNewsletter)}>Confirm Completion</button>
                </div>
                <div className="task-wrapper">
                  <h3>Bring a Friend to any of the scheduled AMA's</h3>
                  <p>Bring a Friend to any of the scheduled AMA's</p>
                  <label htmlFor="friendUsername">Enter your friend's username</label>
                  <input type="text" className="input-dashboard-standard" id="friendUsername" placeholder="@username" value={taskReferAMA} onChange={(e) => setTaskReferAMA(e.target.value)} />
                  <button id="btn-task-refer-ama" className="btn-dashboard-standard" disabled={userTaskList === null || userTaskList?.includes(24)} onClick={() => confirmTask(24, taskReferAMA)}>Confirm Completion</button>
                </div>
              </div>
              <div id="prvdrResourceMenu" className="bf-provMenu hideWarning">
                <h2>Provider Resources:</h2>
                <hr />
                <p>
                  Are you a Provider and need to upload logo and company information for display in the BioFi ecosystem? Click here:
                  <a href="https://biometricfinancial.org/provider-form/" target="_blank" rel="noreferrer">Provider Form</a>
                </p>
                <p>
                  Interested in advertising in the Krptic wallet? Click here: <a href="https://biometricfinancial.org/advertise/" target="_blank" rel="noreferrer">Advertiser Form</a>
                </p>
                <hr />
                <p style={{ textAlign: 'center' }}>
                  <b>Try PhenixLoc free for 10 days</b>
                </p>
                <img src="./img/usb_apk_qrc.jpg" alt="qr" style={{ width: '100px', display: 'block', margin: 'auto' }} />
                <p style={{ textAlign: 'center' }}>
                  <a href="https://bitli.pro/1sxNY_c3986ea7" className="task11">Android .apk link</a>
                  <br /> https://bitli.pro/1sxNY_c3986ea7
                </p>
                <ul>
                  <li>Android users - scan this QR code with  your camera to download APK file to your phone.</li>
                  <li>iOS users – Coming Soon.</li>
                  <li>To enjoy Ethos Metaverse, please <a href="https://ethosmetaverse.net/Setup/EthosMetaverseV092-Setup.exe" target="_blank" rel="noreferrer" className="task8">click here to download</a> and try for free.</li>
                </ul>
              </div>

              <div id="bfCloseContSucc" className="bf-modalmain hideWarning">
                <div className="bf-modalInner-Top">
                  <div className="bf-mCloseContract">
                    <h2> Closing Contract</h2>
                    <hr />
                    <span>Your contract has been successfully closed! </span>
                    <hr />
                    <button className="bf-btn3" id="closeModal" onclick="bfCloseContSuccWarn()">Close</button>
                  </div>
                </div>
              </div>

              <div id="bfAddToken" className="bf-modalmain" style={addTokensModalActive ? { display: "block" } : { display: "none" }}>
                <div className="bf-modalInner-Top">
                  <div className="bf-mAddTokensContract">
                    <h2> Add Tokens To Contract</h2>
                    <hr />
                    <span>Add Tokens to your contract. </span>
                    <div className="bf-mSpan">
                      <form onSubmit={addTokensSubmit}>
                        <strong>Amount:</strong> <input id="biofiStakingAmount" type="number" min={1} step={1} className="bf-stakeAmtTxt" value={addTokensAmount} onChange={(e) => addTokensChange(e.target.value)} />
                        <button className="bf-btn4" id="addTokensToStake" disabled={addTokensAmount <= 0 || displayStakeData?.stakeReadyToClose}>Add Tokens</button>
                      </form>

                    </div>
                    <hr />
                    <button className="bf-btn3" id="closeModal" onClick={toggleAddTokensModal}>Close</button>
                  </div>
                </div>
              </div>

              <div id="bfAddTokenWarn" className="bf-modalmain hideWarning">
                <div className="bf-modalInner-Top">
                  <div className="bf-mCloseContract">
                    <h2> Adding Tokens</h2>
                    <hr />
                    <span>Please wait while your tokens are added to your contract! </span>
                    <div className="bf-mSpan"><strong>Notice:</strong>  Once your have added tokens they are locked for the remainder of your contract period.</div>
                    <hr />
                    <button className="bf-btn3" id="closeModal" onclick="bfCloseAddTokenWarn()">Close</button>
                  </div>
                </div>
              </div>

              <div id="bfUpgradeContract" className="bf-modalmain" style={upgradeModalActive ? { display: "block" } : { display: "none" }}>
                <div className="bf-modalInner-Top">
                  <div className="bf-mCloseContract">
                    <h2> Upgrade Contract</h2>
                    <hr />
                    <span>Select Your New Teir: </span>
                    <div className="bf-mSpan"><strong>Notice:</strong> once you upgrade your rewards contract you can not downgrade.</div>
                    <select name="upgradeTier" id="upgradeTier" value={selectedTier} onChange={(e) => selectedTierChange(e.target.value)}>
                      <option value={0}>Gold</option>
                      <option value={1}>Platinum</option>
                      <option value={2}>Diamond</option>
                      <option value={3}>Emerald</option>
                    </select>
                    <hr />
                    <button className="bf-btn3" onClick={upgradeStake}>Upgrade</button>
                    <button className="bf-btn3" id="closeModal" onClick={toggleUpgradeModal}>Close</button>
                  </div>
                </div>
              </div>

              <div className="popup1" id="findBiofi">
                <div className="bf-popup1-adj">
                  <div className="bf-popup-inner">
                    <h3>Find $BioFi</h3>
                    <div className="bf-findlinksect">
                      <a className="bf-flink" href="https://app.uniswap.org/swap?outputCurrency=0x11A31b833d43853f8869C9EEc17F60e3B4d2a753&chain=bnb"> Uniswap </a> |
                      <a className="bf-flink" href="https://www.mexc.com/exchange/BIOFI_USDT"> MexC Global </a>
                    </div>
                    <div className="bf-close"> <a href="#" className="bf-popclose"> X </a></div>
                  </div>
                </div>
              </div>
              <div className="popup1" id="biofiPerks" style={perksModalActive ? { display: "block" } : { display: "none" }}>
                <div className="bf-popup1-adj">
                  <div className="bf-popup-inner">
                    <div className="stk-dl1">
                      <h2>Perks:</h2>
                      Ethos Metaverse download:
                      <a href="https://ethosmetaverse.net/Setup/EthosMetaverseV092-Setup.exe" className="task8"> <img className="stk-dl-btn" src={ethosDownload} alt="Ethos Download" /></a>
                    </div>
                    <div className="stk-dl1">
                      PhenixLoc Download:
                      <div className="stk-dl-os">
                        <a href="#" className="task11"><img src={androidAPKDownload} alt="Download Android APK" className="stk-dl-btn" /></a>
                        <a href="#" className="task11"><img src={iosDownload} alt="Download from AppStore" className="stk-dl-btn" /></a>
                      </div>
                    </div>
                    <div className="stk-dl1">
                      Krptic Wallet Download:
                      <div className="stk-dl-os">
                        <a href="https://apps.apple.com/gb/app/krptic-wallet/id1571851595"><img src={iosDownload} alt="Download from AppStore" className="stk-dl-btn" /></a>
                      </div>
                      <div className="stk-dl-os">
                        <a href="https://play.google.com/store/apps/details?id=com.krptic&hl=en&gl=us"><img src={androidDownload} alt="Download Android APK" className="stk-dl-btn" /></a>
                        <a href="https://www.krptic.com/krptic.apk"><img src={androidAPKDownload} alt="Download Android APK" className="stk-dl-btn" /></a>
                      </div>
                    </div>
                    <div className="bf-close"> <button className="bf-popclose button-link" onClick={togglePerksModal}> X </button></div>
                  </div>
                </div>
              </div>

            </main>

          </>
        ) : (
          <SwitchNetwork validChain={validChain} />
        )
      }
      <Footer />
      <SessionHandler />
    </>
  );
}